import { FC, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserTie,
  faBullseye,
  faCloud,
  faGift,
  faGlobe,
  faStore,
  faImage,
} from '@fortawesome/free-solid-svg-icons';
import EmployeesPanel from './accountSetup/EmployeesPanel';
import PromotionsPanel from './accountSetup/PromotionsPanel';
import DreamPricesPanel from './accountSetup/DreamPricesPanel';
import ComplimentaryReasonsPanel from './accountSetup/ComplimentaryReasonsPanel';
import EmployeesDetailPanel from './accountSetup/EmployeesDetailPanel';
import PromotionsDetailPanel from './accountSetup/PromotionsDetailPanel';
import DreamPricesDetailPanel from './accountSetup/DreamPricesDetailPanel';
import CountriesPanel from './accountSetup/CountriesPanel';
import LocationsPanel from './accountSetup/LocationsPanel';
import BannersPanel from './accountSetup/BannersPanel';

const NAV_LINKS = [
  {
    key: 'employees',
    label: 'Employees',
    icon: faUserTie,
    description: 'Invite collaborators, assign roles, and manage access policies.',
  },
  {
    key: 'promotions',
    label: 'Promotions',
    icon: faBullseye,
    description: 'Configure promotion strategies and workflows for offers.',
  },
  {
    key: 'dream-prices',
    label: 'Dream Prices',
    icon: faCloud,
    description: 'Maintain aspirational pricing targets and approval cadence.',
  },
  {
    key: 'complimentary-item-reasons',
    label: 'Complimentary Item Reasons',
    icon: faGift,
    description: 'Standardise rationale codes for issuing complimentary items.',
  },
  {
    key: 'countries',
    label: 'Countries',
    icon: faGlobe,
    description: 'Reference list of countries and calling codes.',
  },
  {
    key: 'locations',
    label: 'Locations',
    icon: faStore,
    description: 'Store locations/branches synced from ERP.',
  },
  {
    key: 'banners',
    label: 'Banners',
    icon: faImage,
    description: 'Manage promotional banner images shown to customers.',
  },
];

const AccountSetup: FC = () => {
  const navigate = useNavigate();
  const { section, id } = useParams<{ section?: string; id?: string }>();

  useEffect(() => {
    if (!section) {
      navigate(`/account-setup/${NAV_LINKS[0].key}`, { replace: true });
    }
  }, [section, navigate]);

  const activeKey = useMemo(() => {
    const normalized = section?.toLowerCase();
    return NAV_LINKS.some((link) => link.key === normalized)
      ? (normalized as string)
      : NAV_LINKS[0].key;
  }, [section]);

  const activeLink = useMemo(
    () => NAV_LINKS.find((link) => link.key === activeKey) ?? NAV_LINKS[0],
    [activeKey],
  );

  const showDetail = Boolean(id);

  const handleSelect = (key: string) => {
    if (key === activeKey) {
      return;
    }

    navigate(`/account-setup/${key}`);
  };

  return (
    <div className="rounded-[28px] border border-slate-200/70 bg-white/95 p-8 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)] min-h-[calc(100vh-220px)]">
      <div className="flex h-full flex-col gap-6 md:flex-row">
        <aside className="hidden basis-40 shrink-0 flex-col gap-1.5 rounded-2xl border border-slate-200/70 bg-[#f3f5ff] p-3 shadow-sm lg:flex">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Account setup</h2>
          {NAV_LINKS.map((item) => {
            const isActive = item.key === activeKey;
            return (
              <button
                key={item.key}
                type="button"
                className={`flex items-center gap-3 rounded-xl px-3 py-2 text-left transition ${isActive
                  ? 'bg-primary/10 text-primary shadow-sm'
                  : 'text-slate-600 hover:bg-white/70 hover:text-primary'
                  }`}
                onClick={() => handleSelect(item.key)}
              >
                <span className="text-lg">
                  <FontAwesomeIcon icon={item.icon} className="w-5 h-5" />
                </span>
                <span className="text-sm font-medium">{item.label}</span>
              </button>
            );
          })}
        </aside>

        <section className="flex-1 min-w-0 rounded-2xl border border-slate-200/70 bg-white p-1 shadow-sm">
          {!showDetail && (
            <header className="border-b border-slate-200 p-5 shadow-sm">
              <h1 className="text-2xl font-semibold text-slate-800">{activeLink.label}</h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-500">{activeLink.description}</p>
            </header>
          )}

          <div className="p-5">
            {showDetail ? (
              activeLink.key === 'employees' ? (
                <EmployeesDetailPanel
                  id={id as string}
                  onBack={() => navigate('/account-setup/employees')}
                />
              ) : activeLink.key === 'promotions' ? (
                <PromotionsDetailPanel
                  id={id as string}
                  onBack={() => navigate('/account-setup/promotions')}
                />
              ) : activeLink.key === 'dream-prices' ? (
                <DreamPricesDetailPanel
                  id={id as string}
                  onBack={() => navigate('/account-setup/dream-prices')}
                />
              ) : (
                <div className="rounded-2xl border border-dashed border-primary/15 bg-white p-6 text-sm text-slate-500">
                  Detailed view not available for this section.
                </div>
              )
            ) : activeLink.key === 'employees' ? (
              <EmployeesPanel onSelect={(employeeId) => navigate(`/account-setup/employees/${employeeId}`)} />
            ) : activeLink.key === 'promotions' ? (
              <PromotionsPanel onSelect={(promotionId) => navigate(`/account-setup/promotions/${promotionId}`)} />
            ) : activeLink.key === 'dream-prices' ? (
              <DreamPricesPanel onSelect={(dreamPriceId) => navigate(`/account-setup/dream-prices/${dreamPriceId}`)} />
            ) : activeLink.key === 'complimentary-item-reasons' ? (
              <ComplimentaryReasonsPanel />
            ) : activeLink.key === 'countries' ? (
              <CountriesPanel />
            ) : activeLink.key === 'locations' ? (
              <LocationsPanel />
            ) : (
              <BannersPanel />
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AccountSetup;
